<template>
  <component-wrapper
    class="reduced-layout-fi"
    :force-theme="brand || undefined"
  >
    <div class="reduced-layout-fi__container">
      <div class="reduced-layout-fi__header">
        <img
          v-if="logoSrc"
          class="reduced-layout-fi__header-icon"
          :src="logoSrc"
          :alt="logoAlt || 'logo'"
        />
      </div>
      <div class="reduced-layout-fi__content-container">
        <div class="reduced-layout-fi__content">
          <grid-container-fi :container="false">
            <template v-if="apiData?.Maintenance_Mode">
              <div class="reduced-layout-fi__maintenance-message">
                <result-status-fi :custom-icon="'alert-triangle'">
                  <richtext-fi :html-content="{ value: apiData.Notification }" />
                </result-status-fi>
              </div>
            </template>
            <template v-else>
              <div
                v-if="apiData?.Notification"
                class="reduced-layout-fi__notification"
              >
                <block-notification-fi
                  icon="alert-triangle"
                  type="error"
                >
                  <richtext-fi :html-content="{ value: apiData.Notification }" />
                </block-notification-fi>
              </div>
              <div class="reduced-layout-fi__page">
                <slot />
              </div>
            </template>
          </grid-container-fi>
        </div>
      </div>
      <div
        v-if="apiData"
        class="reduced-layout-fi__footer"
      >
        <div class="reduced-layout-fi__footer-content">
          <div
            v-if="apiData.Available_Languages?.length"
            class="reduced-layout-fi__language"
          >
            <select-input-fi
              value="interfaceLanguage"
              :name="apiData.Language_Selector_Label"
              :options="
                apiData.Available_Languages.map((pageLang) => ({
                  name: pageLang.Title,
                  value: pageLang.Value,
                }))
              "
              class="reduced-layout-fi__lang-selector"
              transparent
              small
              @change="(evt: ChangeLanguageEvent) => $emit('change-language', evt)"
            />
          </div>
          <span class="reduced-layout-fi__footer-item">{{
            $dict.replace(apiData.Company || '', [String(new Date().getFullYear())])
          }}</span>
          <link-fi
            v-for="link in apiData.Elements || []"
            :key="link.Id"
            :href="link.Link.Url"
            :target="link.Link.Target"
            :size="14"
            class="reduced-layout-fi__footer-item"
            text-link
            thin
          >
            {{ link.Link.Title || link.Link.Text }}
          </link-fi>
        </div>
      </div>
    </div>
  </component-wrapper>
</template>

<script setup lang="ts">
import { LinkFi, RichtextFi, SelectInputFi } from 'atoms';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import BlockNotificationFi from '@/components/notifications-fi/block-notification-fi/BlockNotificationFi.vue';
import ResultStatusFi from '@/components/result-status-fi/ResultStatusFi.vue';

interface LanguageOption {
  Title: string;
  Value: string;
}

interface FooterLink {
  Id: string;
  Link: {
    Url: string;
    Target?: string;
    Title?: string;
    Text?: string;
  };
}

interface ApiData {
  Maintenance_Mode?: boolean;
  Notification?: string;
  Available_Languages?: LanguageOption[];
  Language_Selector_Label?: string;
  Company?: string;
  Elements?: FooterLink[];
}

interface ChangeLanguageEvent {
  value: string;
}

interface Props {
  logoSrc?: string | null;
  logoAlt?: string | null;
  apiData?: ApiData | null;
  brand?: string | null;
  interfaceLanguage?: string | null;
}

withDefaults(defineProps<Props>(), {
  logoSrc: null,
  logoAlt: null,
  apiData: null,
  brand: null,
  interfaceLanguage: null,
});

defineEmits<{
  'change-language': [event: ChangeLanguageEvent];
}>();
</script>

<style scoped src="./reduced-layout-fi" />
